import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openPanel, closePanel } from "../../../slices/panelSlice";
import { RootState } from "../../../Redux/store";
import { Tooltip as ReactTooltip, Tooltip } from "react-tooltip";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import API_BASE_URL from "../../../config";
import "./Template.css";
import "./Template.new.css";
import { BlueprintsEmptyState, BlueprintsList } from "./Template.new";
import { useCreditCheck } from "../../../hooks/useCreditCheck";
import { useAppModal } from "../../../hooks/useAppModal";
import EmailCampaignBuilder from "./EmailCampaignBuilder";
import PaginationControls from "../PaginationControls";
import duplicateIcon from "../../../assets/images/icons/duplicate.png";
import CreditCheckModal from "../../common/CreditCheckModal";
import deleteIcon from "../../../assets/images/deleteiconn.png";
import CommonSidePanel from "../../common/CommonSidePanel";
import { defaultButtonStyle } from "../../../styles/buttonStyles";
import { formatUserDate } from "../../common/dateTimePreferences";
import {
  faAngleRight,
  faBars,
  faBullhorn,
  faDashboard,
 // faEdit,
  faEllipsisV,
  faEnvelope,
  faEnvelopeOpen,
  faFileAlt,
  faGear,
  faList,
  faRobot,
  faTrash ,
  faThumbtack, // Add this for Campaign Builder
  faThumbtackSlash,
  faPencil ,
  faPen

} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit,faTrashAlt,faCircleXmark ,faFileLines   } from "@fortawesome/free-regular-svg-icons";
const menuBtnStyle = {
  width: "100%",
  padding: "8px 18px",
  textAlign: "left",
  background: "none",
  border: "none",
  color: "#222",
  fontSize: "15px",
  cursor: "pointer",
} as React.CSSProperties;
const actionIconStyle = {
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
interface Prompt {
  id: number;
  name: string;
  text: string;
  userId?: number;
  createdAt?: string;
  template?: string;
  description?: string;
}

interface CampaignTemplate {
  id: number;
  clientId: string;
  templateName: string;
  // Name of the base template definition this blueprint was created from.
  templateDefinitionName?: string;
  aiInstructions: string;
  placeholderListInfo: string;
  masterBlueprintUnpopulated: string;
  placeholderListWithValue: string;
  campaignBlueprint: string;
  placeholderValues?: Record<string, string>;
  selectedModel: string;
  createdAt: string;
  updatedAt?: string;
  hasConversation?: boolean;
}

// ✅ NEW: Template Definition interface
const dedupeCampaignTemplatesById = (
  templates: CampaignTemplate[],
): CampaignTemplate[] => {
  const seen = new Set<number>();

  return templates.filter((template) => {
    if (seen.has(template.id)) {
      return false;
    }

    seen.add(template.id);
    return true;
  });
};

interface TemplateDefinition {
  id: number;
  templateName: string;
  aiInstructions: string;
  aiInstructionsForEdit: string;
  placeholderList: string;
  masterBlueprintUnpopulated: string;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
  usageCount: number;
}

// Builder/conversation session keys that must be dropped whenever we leave a
// blueprint or swap the builder over to a different one, so the next blueprint
// never inherits stale state.
const BUILDER_SESSION_KEYS = [
  // Active campaign / navigation
  "newCampaignId",
  "newCampaignName",
  "autoStartConversation",
  "openConversationTab",
  "initialExampleEmail",
  // Conversation + derived state (useSessionState keys)
  "campaign_messages",
  "campaign_final_prompt",
  "campaign_final_preview",
  "campaign_placeholder_values",
  "campaign_is_complete",
  "campaign_started",
  "campaign_system_prompt",
  "campaign_system_prompt_edit",
  "campaign_master_prompt",
  "campaign_master_prompt_extensive",
  "campaign_preview_text",
  "campaign_selected_model",
  "campaign_template_name",
  "campaign_web_search_instructions",
  "campaign_activeMainTab",
  "campaign_activeBuildTab",
];

// Note: keeps selectedTemplateDefinitionId and campaign_sound_enabled
// (needed next time / user preference).
const clearBuilderSessionState = () => {
  BUILDER_SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

interface TemplateProps {
  selectedClient: string;
  userRole?: string;
  isDemoAccount?: boolean;
  onTemplateSelect?: (prompt: Prompt) => void;
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ script: "sub" }, { script: "super" }],
    ["clean"],
  ],
};

const Template: React.FC<TemplateProps> = ({
  selectedClient,
  userRole = "USER",
  isDemoAccount = false,
  onTemplateSelect,
}) => {
  // States
  const [campaignTemplates, setCampaignTemplates] = useState<
    CampaignTemplate[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCampaignTemplate, setSelectedCampaignTemplate] =
    useState<CampaignTemplate | null>(null);
  const [templateActionsAnchor, setTemplateActionsAnchor] = useState<
    string | null
  >(null);

  // Modal states
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showViewCampaignModal, setShowViewCampaignModal] = useState(false);
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  // const [showRenameModal, setShowRenameModal] = useState(false);
  const dispatch = useDispatch();
  const activePanel = useSelector((state: RootState) => state.panel.activePanel);
  const showRenameModal = activePanel === "rename-blueprint";
  const showTemplateNameModal = activePanel === "template-name";
  const showCloneNameModal = activePanel === "clone-blueprint";
  const [renameInput, setRenameInput] = useState("");
  const [showCloneConfirmModal, setShowCloneConfirmModal] = useState(false);
  // const [showCloneNameModal, setShowCloneNameModal] = useState(false);
  const [cloneNameInput, setCloneNameInput] = useState("");

  const [viewCampaignTab, setViewCampaignTab] = useState<
    "example" | "template"
  >("example");
  const [exampleEmail, setExampleEmail] = useState("");
  const [editableCampaignTemplate, setEditableCampaignTemplate] = useState("");
  const [currentPlaceholderValues, setCurrentPlaceholderValues] = useState<
    Record<string, string>
  >({});

  // The AI model is no longer part of this form — it is set application-wide in
  // Settings > AI models.
  const [editCampaignForm, setEditCampaignForm] = useState({
    templateName: "",
    aiInstructions: "",
    placeholderListInfo: "",
    masterBlueprintUnpopulated: "",
  });

  const appModal = useAppModal();
  const {
    checkUserCredits,
    showCreditModal,
    closeCreditModal,
    handleSkipModal,
    credits,
  } = useCreditCheck();
  const [forceShowCreditModal, setForceShowCreditModal] = useState(false);
  const handleCreditModalTabChange = (nextTab: string) => {
    if (nextTab === "MyPlan") {
      sessionStorage.setItem("forceMyPlanRedirect", "true");
      const appBaseUrl = window.location.href.split("#")[0];
      window.location.href = `${appBaseUrl}#/main?tab=MyPlan`;
    }
  };
  const createCampaignClickInFlightRef = useRef(false);
  const DEFAULT_USER_TEMPLATE_ID = 65;
  const DEFAULT_USER_TEMPLATE_NAME = "PKB- FINAL 2.0";
  const isAdmin = userRole?.toUpperCase() === "ADMIN";
  const normalizeClientId = (value?: string | null) => {
    const trimmedValue = value?.trim();

    if (
      !trimmedValue ||
      trimmedValue === "null" ||
      trimmedValue === "undefined"
    ) {
      return "";
    }

    return trimmedValue;
  };
  const loggedInClientId = normalizeClientId(sessionStorage.getItem("clientId"));
  const selectedClientId = normalizeClientId(selectedClient);
  const effectiveUserId = isAdmin
    ? selectedClientId || loggedInClientId
    : loggedInClientId;

  const BLUEPRINT_BUILDER_SESSION_KEY = "blueprintBuilderOpen";

  const canGenerateFromCreditResponse = (creditResponse: any) => {
    if (typeof creditResponse === "number") {
      return creditResponse > 0;
    }

    if (creditResponse && typeof creditResponse === "object") {
      const totalCredits = Number(creditResponse.total ?? 0);
      return creditResponse.canGenerate !== false && totalCredits > 0;
    }

    return false;
  };

  const getStoredActiveBlueprintId = () => {
    const storedId =
      sessionStorage.getItem("newCampaignId") ||
      sessionStorage.getItem("editTemplateId");
    const parsedId = Number(storedId);

    return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
  };

  const shouldRestoreCampaignBuilder = () => {
    const hasActiveBlueprint = getStoredActiveBlueprintId() !== null;
    const builderWasOpen =
      sessionStorage.getItem(BLUEPRINT_BUILDER_SESSION_KEY) === "true";
    const editModeRequested =
      sessionStorage.getItem("editTemplateMode") === "true";

    return hasActiveBlueprint && (builderWasOpen || editModeRequested);
  };

  const [showCampaignBuilder, setShowCampaignBuilder] = useState(() =>
    shouldRestoreCampaignBuilder(),
  );

  // ✅ NEW: Template name modal states
  // const [showTemplateNameModal, setShowTemplateNameModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState("");
  const [templateDefinitions, setTemplateDefinitions] = useState<
    TemplateDefinition[]
  >([]);
  const [selectedTemplateDefinitionId, setSelectedTemplateDefinitionId] =
    useState<number | null>(null);
  const [isPreparingCreateCampaign, setIsPreparingCreateCampaign] =
    useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [hoveredTemplateId, setHoveredTemplateId] = useState<number | null>(
    null,
  );
  const [exampleCache, setExampleCache] = useState<
    Record<number, string | undefined>
  >({});
  // Category filter — the base template ("category") a blueprint was created from.
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
   // ✅ NEW: Sorting state for templates table
  const [listSortKey, setListSortKey] = useState<string>("templateName");
  const [listSortDirection, setListSortDirection] = useState<"asc" | "desc">("asc");

   // Utility functions
  // ✅ NEW: String comparison helper for sorting
  const compareStrings = (a?: string, b?: string, direction: "asc" | "desc" = "asc") => {
    const valueA = (a || "").toLowerCase();
    const valueB = (b || "").toLowerCase();

    if (valueA < valueB) return direction === "asc" ? -1 : 1;
    if (valueA > valueB) return direction === "asc" ? 1 : -1;
    return 0;
  };
  // ✅ NEW: Handle column sort
  const handleListSort = (key: string) => {
  if (listSortKey === key) {
    setListSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  } else {
    setListSortKey(key);
    setListSortDirection("asc");
  }
};

  // Utility functions
  const formatDate = formatUserDate;

  // ✅ NEW: Fetch template definitions
  const fetchTemplateDefinitions = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/template-definitions?activeOnly=true`,
      );

      if (!response.ok) throw new Error("Failed to load template definitions");

      const data = await response.json();
      const defs: TemplateDefinition[] = data.templateDefinitions || [];

      if (isAdmin) {
        // ✅ Admin → see all templates
        setTemplateDefinitions(defs);

        if (defs.length > 0) {
          setSelectedTemplateDefinitionId(defs[0].id);
        }
      } else {
        // ✅ USER → force PKB-Final only
        const pkbTemplate = defs.find((d) => d.id === DEFAULT_USER_TEMPLATE_ID);

        setTemplateDefinitions(pkbTemplate ? [pkbTemplate] : []);
        setSelectedTemplateDefinitionId(DEFAULT_USER_TEMPLATE_ID);
      }
    } catch (error) {
      console.error(error);
      setTemplateDefinitions([]);
    }
  }, [isAdmin]);

  // Fetch campaign templates
  const fetchCampaignTemplates = useCallback(async () => {
    if (!effectiveUserId) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/templates/${effectiveUserId}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCampaignTemplates(
        dedupeCampaignTemplatesById(data.templates || []),
      );
    } catch (error) {
      console.error("Error fetching campaign templates:", error);
      setCampaignTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveUserId]);

  // ✅ NEW: Handle create campaign button click
  const handleCreateCampaignClick = async () => {
    if (createCampaignClickInFlightRef.current) {
      return;
    }

    createCampaignClickInFlightRef.current = true;

    if (!effectiveUserId) {
      appModal.showError(
        isAdmin
          ? "Please select a client or log in again to use the admin account."
          : "Client ID missing. Please log in again.",
      );
      createCampaignClickInFlightRef.current = false;
      return;
    }

    try {
      // Check credits before allowing blueprint creation
      if (sessionStorage.getItem("isDemoAccount") !== "true" && effectiveUserId) {
        const currentCredits = await checkUserCredits(effectiveUserId);

        if (!canGenerateFromCreditResponse(currentCredits)) {
          setForceShowCreditModal(true);
          return; // Stop execution if can't generate
        }
      }

      setIsPreparingCreateCampaign(true);
      dispatch(openPanel("template-name"));
      setTemplateNameInput("");
      setSelectedTemplateDefinitionId(null);
      setTemplateDefinitions([]);

      await fetchTemplateDefinitions();
    } finally {
      setIsPreparingCreateCampaign(false);
      createCampaignClickInFlightRef.current = false;
    }
  };

  const ensureCanOpenAiChat = async () => {
    if (sessionStorage.getItem("isDemoAccount") === "true") {
      return true;
    }

    if (!effectiveUserId) {
      appModal.showError(
        isAdmin
          ? "Please select a client or log in again to use the admin account."
          : "Client ID missing. Please log in again.",
      );
      return false;
    }

    const currentCredits = await checkUserCredits(effectiveUserId);

    const canOpenChat = canGenerateFromCreditResponse(currentCredits);

    if (!canOpenChat) {
      setForceShowCreditModal(true);
    }

    return canOpenChat;
  };

  // ✅ NEW: Handle template name submission
  // ✅ UPDATED: Handle template name submission
  const handleTemplateNameSubmit = async () => {
    if (!templateNameInput.trim()) {
      appModal.showError("Please enter a campaign name");
      return;
    }

    if (!effectiveUserId) {
      appModal.showError(
        isAdmin
          ? "Please select a client or log in again to use the admin account."
          : "Client ID missing. Please log in again.",
      );
      return;
    }

    // ✅ ROLE-BASED TEMPLATE RESOLUTION (TS SAFE)
    let finalTemplateDefinitionId: number;

    if (isAdmin) {
      if (selectedTemplateDefinitionId == null) {
        appModal.showError("Please select a template definition first");
        return;
      }
      finalTemplateDefinitionId = selectedTemplateDefinitionId;
    } else {
      // 🔒 USER → force PKB-Final
      finalTemplateDefinitionId = DEFAULT_USER_TEMPLATE_ID;
    }

    setIsCreatingCampaign(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/campaign/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: effectiveUserId,
            templateDefinitionId: finalTemplateDefinitionId,
            templateName: templateNameInput,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to create campaign");
      }

      const data = await response.json();

      if (data.success || data.Success) {
        // ✅ Store campaign info
        sessionStorage.setItem("newCampaignId", data.campaignId.toString());
        sessionStorage.setItem("newCampaignName", data.templateName);
        sessionStorage.setItem(
          "selectedTemplateDefinitionId",
          finalTemplateDefinitionId.toString(),
        );
        sessionStorage.setItem("autoStartConversation", "true");
        sessionStorage.setItem("openConversationTab", "true");
        setActiveBlueprintId(data.campaignId);

        await fetchCampaignTemplates(); // ✅ CRITICAL

        // ✅ Close modal and open builder

        dispatch(closePanel());
        setShowCampaignBuilder(true);

      } 
      
      else {
        throw new Error(
          data.message || data.Message || "Failed to create campaign",
        );
      }
    } catch (error) {
      console.error("Error creating campaign:", error);
      appModal.showError("Failed to create campaign");
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  // Update template
  const handleUpdateCampaignTemplate = async () => {
    if (!selectedCampaignTemplate || !editCampaignForm.templateName) {
      appModal.showError("Please fill all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/template/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: selectedCampaignTemplate.id,
            templateName: editCampaignForm.templateName,
            aiInstructions: editCampaignForm.aiInstructions,
            placeholderListInfo: editCampaignForm.placeholderListInfo,
            masterBlueprintUnpopulated:
              editCampaignForm.masterBlueprintUnpopulated,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update campaign template");
      }

      appModal.showSuccess("Campaign template updated successfully!");
      setShowEditCampaignModal(false);
      await fetchCampaignTemplates();
    } catch (error) {
      appModal.showError("Failed to update campaign template");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete campaign template
  const handleDeleteCampaignTemplate = async () => {
    if (!selectedCampaignTemplate) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/template/${selectedCampaignTemplate.id}/delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete campaign template");
      }

      appModal.showSuccess("Campaign template deleted successfully!");
      setShowDeleteConfirmModal(false);
      setSelectedCampaignTemplate(null);
      await fetchCampaignTemplates();
    } catch (error) {
      appModal.showError("Failed to delete campaign template");
    } finally {
      setIsLoading(false);
    }
  };

  // Rename campaign template
  const handleRenameCampaignTemplate = async () => {
    if (!selectedCampaignTemplate || !renameInput.trim()) {
      appModal.showError("Please enter a valid name");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/rename-Template`,
        {
          method: "POST",
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            clientId: effectiveUserId,
            templateId: selectedCampaignTemplate.id,
            templateName: renameInput.trim(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to rename campaign template");
      }

      appModal.showSuccess("Campaign template renamed successfully!");
      dispatch(closePanel());
      setSelectedCampaignTemplate(null);
      setRenameInput("");
      await fetchCampaignTemplates();
    } catch (error) {
      appModal.showError("Failed to rename campaign template");
    } finally {
      setIsLoading(false);
    }
  };

  // Clone campaign template with name
  const handleCloneCampaignTemplate = async () => {
    if (!selectedCampaignTemplate || !cloneNameInput.trim()) {
      appModal.showError("Please enter a name for the cloned template");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/clone-template?clientId=${effectiveUserId}&templateId=${selectedCampaignTemplate.id}&Name=${encodeURIComponent(cloneNameInput.trim())}`,
        {
          method: "POST",
          headers: { accept: "*/*" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to clone campaign template");
      }

      appModal.showSuccess("Campaign template cloned successfully!");
      dispatch(closePanel());
      setSelectedCampaignTemplate(null);
      setCloneNameInput("");
      await fetchCampaignTemplates();
    } catch (error) {
      appModal.showError("Failed to clone campaign template");
    } finally {
      setIsLoading(false);
    }
  };

  // Distinct categories (base template names) with their blueprint counts.
  const categoryOptions = React.useMemo(() => {
    const counts = new Map<string, number>();

    campaignTemplates.forEach((template) => {
      const name = (template.templateDefinitionName || "").trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });

    return Array.from(counts, ([name, count]) => ({ name, count })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [campaignTemplates]);

  // Drop the filter if the selected category no longer exists (e.g. after a delete).
  useEffect(() => {
    if (categoryFilter && !categoryOptions.some((c) => c.name === categoryFilter)) {
      setCategoryFilter(null);
    }
  }, [categoryFilter, categoryOptions]);

  // Filter templates
  const filteredCampaignTemplates = campaignTemplates.filter((template) => {
    if (
      categoryFilter &&
      (template.templateDefinitionName || "").trim() !== categoryFilter
    ) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      template.templateName.toLowerCase().includes(searchLower) ||
      (template.templateDefinitionName || "").toLowerCase().includes(searchLower) ||
      template.id.toString().includes(searchLower)
    );
  })// ✅ NEW: Sort filtered templates
    .sort((a, b) => {
      switch (listSortKey) {
        case "templateName":
          return compareStrings(a.templateName, b.templateName, listSortDirection);

        case "templateDefinitionName":
          return compareStrings(
            a.templateDefinitionName,
            b.templateDefinitionName,
            listSortDirection,
          );

        case "id":
          const idCompare = a.id - b.id;
          return listSortDirection === "asc" ? idCompare : -idCompare;

        case "createdAt":
          const dateA = new Date(a.createdAt || "").getTime();
          const dateB = new Date(b.createdAt || "").getTime();
          const dateCompare = dateA - dateB;
          return listSortDirection === "asc" ? dateCompare : -dateCompare;

        default:
          return 0;
      }
    });
  //pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | "All">(10);
  //const pageSize = 10;
  const effectivePageSize =
    pageSize === "All" ? filteredCampaignTemplates.length : pageSize;
  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredCampaignTemplates.length / effectivePageSize);

  const startIndex =
    pageSize === "All" ? 0 : (currentPage - 1) * effectivePageSize;
  const paginatedTemplates =
    pageSize === "All"
      ? filteredCampaignTemplates
      : filteredCampaignTemplates.slice(
          startIndex,
          startIndex + (effectivePageSize || 0),
        );
  // Effects
  useEffect(() => {
    if (effectiveUserId) {
      fetchCampaignTemplates();
    }
  }, [effectiveUserId, fetchCampaignTemplates]);

  const generateExampleEmail = (template: CampaignTemplate) => {
    if (!template) return "";

    if ((template as any).exampleOutput) {
      // ✅ DB field preferred
      return (template as any).exampleOutput;
    }

    if (template.placeholderValues?.example_output) {
      return template.placeholderValues.example_output;
    }

    return template.campaignBlueprint || "";
  };
  const preloadExampleEmail = async (template: CampaignTemplate) => {
    if (template.id in exampleCache) return; // 👈 IMPORTANT

    // Mark as loading
    setExampleCache((prev) => ({
      ...prev,
      [template.id]: undefined,
    }));

    try {
      const fullTemplate = await fetchCampaignTemplateDetails(template.id);

       // ✅ Take ONLY example_output_email from placeholderValues
    const example = fullTemplate?.placeholderValues?.example_output_email || "";

      setExampleCache((prev) => ({
        ...prev,
        [template.id]: example, // empty string means "no example"
      }));
    } catch (e) {
      setExampleCache((prev) => ({
        ...prev,
        [template.id]: "",
      }));
    }
  };

  // ==================================================================
  // BLUEPRINT SWITCHER — picklist next to "Back to blueprints"
  // ==================================================================
  // Swaps the open builder over to another blueprint: the previous blueprint's
  // builder/conversation session state is cleared, the new blueprint's ids and
  // example email are written to sessionStorage, and the builder is remounted
  // (via its `key`) so it reloads everything from the new blueprint. Because
  // `editTemplateMode` is set, the builder runs loadTemplateForEdit — a
  // completed blueprint (one that already has an example output email) opens
  // directly in edit mode.
  const handleBlueprintSwitch = async (blueprintId: number) => {
    const blueprint = campaignTemplates.find((b) => b.id === blueprintId);
    if (!blueprint || blueprintId === activeBlueprintId) return;
    if (isSwitchingBlueprint) return;

    setIsSwitchingBlueprint(true);

    let example = "";
    try {
      const fullTemplate = await fetchCampaignTemplateDetails(blueprintId);
      example = fullTemplate?.placeholderValues?.example_output_email || "";
    } catch (error) {
      console.error("Error loading blueprint example:", error);
    }

    try {
      // Drop the outgoing blueprint's state before the new builder instance
      // reads sessionStorage in its state initialisers.
      clearBuilderSessionState();

      sessionStorage.setItem("newCampaignId", blueprint.id.toString());
      sessionStorage.setItem("newCampaignName", blueprint.templateName || "");
      sessionStorage.setItem("editTemplateId", blueprint.id.toString());
      sessionStorage.setItem("editTemplateMode", "true");
      sessionStorage.setItem("initialExampleEmail", example);
      sessionStorage.setItem(BLUEPRINT_BUILDER_SESSION_KEY, "true");

      setExampleCache((prev) => ({ ...prev, [blueprint.id]: example }));
      setActiveBlueprintId(blueprint.id);
      setShowCampaignBuilder(true);
    } finally {
      setIsSwitchingBlueprint(false);
    }
  };

  const fetchCampaignTemplateDetails = async (templateId: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/campaign/${templateId}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("AI data", data);
      return data;
    } catch (error) {
      console.error("Error fetching template details:", error);
      return null;
    }
  };
  const extractExampleOutputEmail = (placeholderListWithValue?: string) => {
    if (!placeholderListWithValue) return "";

    // Match everything after {example_output_email} =
    const match = placeholderListWithValue.match(
      /\{example_output_email\}\s*=\s*([\s\S]*)/i,
    );

    return match ? match[1].trim() : "";
  };
  const extractExampleFromPlaceholderList = (
    placeholderListWithValue?: any,
  ): string => {
    if (!placeholderListWithValue) return "";

    // CASE 1: API returns ARRAY
    if (Array.isArray(placeholderListWithValue)) {
      const exampleItem = placeholderListWithValue.find(
        (item) =>
          item.key === "example_output_email" ||
          item.name === "example_output_email",
      );

      return exampleItem?.value?.trim() || "";
    }

    // CASE 2: API returns STRING
    if (typeof placeholderListWithValue === "string") {
      const match = placeholderListWithValue.match(
        /\{example_output_email\}\s*=\s*([\s\S]*)/i,
      );
      return match?.[1]?.trim() || "";
    }

    return "";
  };

  const handleViewCampaignTemplate = async (template: CampaignTemplate) => {
    setIsLoading(true);
    try {
      const fullTemplate = await fetchCampaignTemplateDetails(template.id);
      if (fullTemplate) {
        setSelectedCampaignTemplate(fullTemplate);
        setExampleEmail(
          fullTemplate.placeholderValues?.example_output_email || "",
        );

        setEditableCampaignTemplate(fullTemplate.campaignBlueprint || "");
        setCurrentPlaceholderValues(fullTemplate.placeholderValues || {});
        setViewCampaignTab("example");
        setShowViewCampaignModal(true);
      }
    } catch (error) {
      appModal.showError("Failed to load template details");
    } finally {
      setIsLoading(false);
    }
  };
  const getTooltipText = (html?: string) => {
    if (!html || html === "No example email available") {
      return "No example email available";
    }

    const div = document.createElement("div");
    div.innerHTML = html;

    const text = div.textContent || div.innerText || "";
    return text.trim() || "No example email available";
  };

  const handleSaveCampaignTemplateChanges = async () => {
    if (!selectedCampaignTemplate) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/CampaignPrompt/template/update`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: selectedCampaignTemplate.id,
            templateName: selectedCampaignTemplate.templateName,
            aiInstructions: selectedCampaignTemplate.aiInstructions,
            placeholderListInfo: selectedCampaignTemplate.placeholderListInfo,
            masterBlueprintUnpopulated:
              selectedCampaignTemplate.masterBlueprintUnpopulated,
            campaignBlueprint: editableCampaignTemplate,
            selectedModel: selectedCampaignTemplate.selectedModel,
            placeholderValues: currentPlaceholderValues,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update campaign template");
      }

      appModal.showSuccess("Campaign template updated successfully!");
      await fetchCampaignTemplates();
    } catch (error) {
      appModal.showError("Failed to update campaign template");
    } finally {
      setIsLoading(false);
    }
  };
  // Named handler wrappers for BlueprintsList component
  const handleRowOpen = async (template: CampaignTemplate) => {
    sessionStorage.setItem("editTemplateId", template.id.toString());
    sessionStorage.setItem("editTemplateMode", "true");
    sessionStorage.setItem("newCampaignId", template.id.toString());
    sessionStorage.setItem("newCampaignName", template.templateName);
    setActiveBlueprintId(template.id);
    try {
      const full = await fetchCampaignTemplateDetails(template.id);
      sessionStorage.setItem("initialExampleEmail", full?.placeholderValues?.example_output_email || "");
    } catch {
      sessionStorage.setItem("initialExampleEmail", "");
    }
    setShowCampaignBuilder(true);
  };

  const handleRowEdit = handleRowOpen;

  const handleRowRename = (template: CampaignTemplate) => {
    setSelectedCampaignTemplate(template);
    setRenameInput(template.templateName);
    dispatch(openPanel("rename-blueprint"));
  };

  const handleRowClone = (template: CampaignTemplate) => {
    setSelectedCampaignTemplate(template);
    setCloneNameInput(`${template.templateName} - copy`);
    dispatch(openPanel("clone-blueprint"));
  };

  const handleRowDelete = (template: CampaignTemplate) => {
    setSelectedCampaignTemplate(template);
    setShowDeleteConfirmModal(true);
  };

  const [activeBlueprintId, setActiveBlueprintId] = useState<number | null>(() =>
    getStoredActiveBlueprintId(),
  );
  const [isSwitchingBlueprint, setIsSwitchingBlueprint] = useState(false);

  // Blueprints offered by the switcher inside the builder, sorted by name so the
  // picklist is predictable regardless of the list's sort column. Built from
  // campaignTemplates, which is already scoped to effectiveUserId — an admin sees
  // the selected client's blueprints, a normal user only their own.
  const blueprintSwitcherOptions = React.useMemo(() => {
    const options = campaignTemplates.map((template) => ({
      id: template.id,
      templateName: template.templateName || `Blueprint #${template.id}`,
    }));

    // A blueprint that was just created may not be in the fetched list yet —
    // keep it selectable so the dropdown never looks empty.
    if (
      activeBlueprintId !== null &&
      !options.some((option) => option.id === activeBlueprintId)
    ) {
      options.push({
        id: activeBlueprintId,
        templateName:
          sessionStorage.getItem("newCampaignName") ||
          `Blueprint #${activeBlueprintId}`,
      });
    }

    return options.sort((a, b) =>
      a.templateName.localeCompare(b.templateName, undefined, {
        sensitivity: "base",
      }),
    );
  }, [campaignTemplates, activeBlueprintId]);

  const openStoredBlueprintInBuilder = useCallback((templateId: number) => {
    if (!Number.isFinite(templateId) || templateId <= 0) return;

    sessionStorage.setItem("editTemplateId", templateId.toString());
    sessionStorage.setItem("editTemplateMode", "true");
    sessionStorage.setItem("newCampaignId", templateId.toString());
    setActiveBlueprintId(templateId);
    setShowCampaignBuilder(false);
    setTimeout(() => {
      setShowCampaignBuilder(true);
    }, 0);
  }, []);

  // Check if we should open builder directly on mount
  useEffect(() => {
    const storedId =
      sessionStorage.getItem("newCampaignId") ||
      sessionStorage.getItem("editTemplateId");
    const parsedId = Number(storedId);
    const hasStoredBlueprint = Number.isFinite(parsedId) && parsedId > 0;
    const builderWasOpen =
      sessionStorage.getItem(BLUEPRINT_BUILDER_SESSION_KEY) === "true";
    const editModeRequested =
      sessionStorage.getItem("editTemplateMode") === "true";

    if (hasStoredBlueprint) {
      setActiveBlueprintId(parsedId);
    }

    if (hasStoredBlueprint && (builderWasOpen || editModeRequested)) {
      setShowCampaignBuilder(true);
    }
  }, []);

  useEffect(() => {
    const handleSwitchToBlueprint = (event: Event) => {
      const templateId = Number((event as CustomEvent<{ templateId?: number }>).detail?.templateId);
      openStoredBlueprintInBuilder(templateId);
    };

    window.addEventListener("switchToBlueprint", handleSwitchToBlueprint);
    return () => {
      window.removeEventListener("switchToBlueprint", handleSwitchToBlueprint);
    };
  }, [openStoredBlueprintInBuilder]);

  useEffect(() => {
    if (showCampaignBuilder && activeBlueprintId !== null) {
      sessionStorage.setItem(BLUEPRINT_BUILDER_SESSION_KEY, "true");
      return;
    }

    sessionStorage.removeItem(BLUEPRINT_BUILDER_SESSION_KEY);
  }, [activeBlueprintId, showCampaignBuilder]);
  // ✅ NEW: Render sort arrow indicator
  const renderSortArrow = (columnKey: string, currentSortKey: string, sortDirection: string) => {
    if (columnKey === currentSortKey) {
      return sortDirection === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  // Exit the inline campaign builder and return to the blueprints list
  const handleExitBuilder = () => {
    setShowCampaignBuilder(false);
    setActiveBlueprintId(null);
    sessionStorage.removeItem(BLUEPRINT_BUILDER_SESSION_KEY);

    // Clear all builder/conversation state so reopening a blueprint starts fresh
    // (stale session state was causing intermittent bugs). Runs after the builder
    // unmounts so its useSessionState effects don't write the old values back.
    setTimeout(async () => {
      clearBuilderSessionState();
      await fetchCampaignTemplates();
    }, 300);
  };

  return (
    <div className="template-container">
      {!showCampaignBuilder ? (
        <>
          {campaignTemplates.length === 0 && !isLoading ? (
            <BlueprintsEmptyState onCreate={handleCreateCampaignClick} />
          ) : (
            <BlueprintsList
              templates={paginatedTemplates}
              totalCount={filteredCampaignTemplates.length}
              isLoading={isLoading}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortKey={listSortKey}
              sortDirection={listSortDirection}
              onSort={handleListSort}
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onCreateClick={handleCreateCampaignClick}
              categories={categoryOptions}
              activeCategory={categoryFilter}
              onCategoryChange={(category) => {
                setCategoryFilter(category);
                setCurrentPage(1);
              }}
              onRowClick={handleRowOpen}
              templateActionsAnchor={templateActionsAnchor}
              setTemplateActionsAnchor={setTemplateActionsAnchor}
              onView={handleViewCampaignTemplate}
              onEdit={handleRowEdit}
              onRename={handleRowRename}
              onClone={handleRowClone}
              onDelete={handleRowDelete}
              formatDate={formatDate}
              preloadExampleEmail={preloadExampleEmail}
              exampleCache={exampleCache}
            />
          )}

          <CommonSidePanel
            isOpen={showTemplateNameModal}
            onClose={() => dispatch(closePanel())}
            title="📝 Create new campaign blueprint"
            width={440}
            footerContent={
              <>
                <button
                  className="button secondary"
                  onClick={() => dispatch(closePanel())}
                  disabled={isCreatingCampaign}
                  style={{ 
                    padding: "10px 24px", 
                    borderRadius: "24px",
                    border: "2px solid #ddd",
                    background: "#fff",
                    color: "#666",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleTemplateNameSubmit}
                  disabled={
                    isPreparingCreateCampaign ||
                    !templateNameInput.trim() ||
                    !selectedTemplateDefinitionId ||
                    isCreatingCampaign
                  }
                  style={{
                    ...defaultButtonStyle,
                    cursor: (isPreparingCreateCampaign || !templateNameInput.trim() || !selectedTemplateDefinitionId || isCreatingCampaign) ? "not-allowed" : "pointer",
                    opacity: (isPreparingCreateCampaign || !templateNameInput.trim() || !selectedTemplateDefinitionId || isCreatingCampaign) ? 0.5 : 1
                  }}
                >
                  {isCreatingCampaign ? (
                    <>
                      <span className="spinner" style={{ marginRight: "8px" }}>⏳</span>
                      Creating campaign...
                    </>
                  ) : (
                    "Create blueprint"
                  )}
                </button>
              </>
            }
          >
            {isPreparingCreateCampaign ? (
              <div
                style={{
                  minHeight: "220px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "14px",
                  color: "#4b5563",
                }}
              >
                <style>
                  {`
                    @keyframes templatePanelSpin {
                      to { transform: rotate(360deg); }
                    }
                  `}
                </style>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    border: "4px solid #e5e7eb",
                    borderTopColor: "#dc3545",
                    borderRadius: "50%",
                    animation: "templatePanelSpin 0.8s linear infinite",
                  }}
                />
                <div style={{ fontSize: "14px", fontWeight: 500 }}>
                  Loading blueprint options...
                </div>
              </div>
            ) : (
              <>
                {isAdmin && (
                  <div className="form-group" style={{ marginBottom: "20px" }}>
                    <label>
                      Select base template <span style={{ color: "red" }}>*</span>
                    </label>
                    <select
                      value={selectedTemplateDefinitionId || ""}
                      onChange={(e) => setSelectedTemplateDefinitionId(parseInt(e.target.value))}
                    >
                      <option value="">-- Select a template definition --</option>
                      {templateDefinitions.map((def) => (
                        <option key={def.id} value={def.id}>
                          {def.templateName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label htmlFor="templateName" style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                    Blueprint name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    id="templateName"
                    type="text"
                    value={templateNameInput}
                    onChange={(e) => setTemplateNameInput(e.target.value)}
                    placeholder="e.g., IBM Sales Outreach"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && templateNameInput.trim() && selectedTemplateDefinitionId) {
                        handleTemplateNameSubmit();
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "15px",
                    }}
                  />
                  <p style={{ marginTop: "8px", fontSize: "13px", color: "#6b7280" }}>
                    💡 Give this specific campaign instance a unique name
                  </p>
                </div>
              </>
            )}
          </CommonSidePanel>
          {/* Clone Name Input Modal */}
          <CommonSidePanel
            isOpen={showCloneNameModal && selectedCampaignTemplate !== null}
            onClose={() => {
              dispatch(closePanel());
              setSelectedCampaignTemplate(null);
              setCloneNameInput("");
            }}
            title="Clone blueprint"
            width={440}
            footerContent={
              <>
                <button
                  onClick={() => {
                    dispatch(closePanel());
                    setSelectedCampaignTemplate(null);
                    setCloneNameInput("");
                  }}
                  disabled={isLoading}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "24px",
                    border: "2px solid #ddd",
                    background: "#fff",
                    color: "#666",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCloneCampaignTemplate}
                  disabled={isLoading || !cloneNameInput.trim()}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "24px",
                    border: "2px solid #dc3545",
                    background: "#fff",
                    color: "#dc3545",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: cloneNameInput.trim() ? "pointer" : "not-allowed",
                    opacity: cloneNameInput.trim() ? 1 : 0.5
                  }}
                >
                  {isLoading ? "Cloning..." : "Clone Blueprint"}
                </button>
              </>
            }
          >
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label
                htmlFor="cloneNameInput"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#374151",
                }}
              >
                New blueprint name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="cloneNameInput"
                type="text"
                value={cloneNameInput}
                onChange={(e) => setCloneNameInput(e.target.value)}
                placeholder="Enter name for cloned blueprint"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter" && cloneNameInput.trim()) {
                    handleCloneCampaignTemplate();
                  }
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              <p style={{ marginTop: "8px", fontSize: "13px", color: "#6b7280" }}>
                💡 Cloning from: <strong>{selectedCampaignTemplate?.templateName}</strong>
              </p>
            </div>
          </CommonSidePanel>

          {/* Rename Modal */}
          <CommonSidePanel
            isOpen={showRenameModal && selectedCampaignTemplate !== null}
            onClose={() => {
              dispatch(closePanel());
              setSelectedCampaignTemplate(null);
              setRenameInput("");
            }}
            title="Rename blueprint"
            width={440}
            footerContent={
              <>
                <button
                  onClick={() => {
                    dispatch(closePanel());
                    setSelectedCampaignTemplate(null);
                    setRenameInput("");
                  }}
                  disabled={isLoading}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "24px",
                    border: "2px solid #ddd",
                    background: "#fff",
                    color: "#666",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRenameCampaignTemplate}
                  disabled={isLoading || !renameInput.trim()}
                  style={{
                    padding: "10px 24px",
                    borderRadius: "24px",
                    border: "2px solid #dc3545",
                    background: "#fff",
                    color: "#dc3545",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: renameInput.trim() ? "pointer" : "not-allowed",
                    opacity: renameInput.trim() ? 1 : 0.5
                  }}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </>
            }
          >
            <div className="form-group" style={{ marginBottom: "16px" }}>
              <label
                htmlFor="renameInput"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#374151",
                }}
              >
                Blueprint name <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                id="renameInput"
                type="text"
                value={renameInput}
                onChange={(e) => setRenameInput(e.target.value)}
                placeholder="Enter blueprint name"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === "Enter" && renameInput.trim()) {
                    handleRenameCampaignTemplate();
                  }
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
          </CommonSidePanel>

          {/* Delete Confirmation Modal */}
          {showDeleteConfirmModal && selectedCampaignTemplate && (
            <div className="modal-backdrop">
              <div className="modal-content" style={{ padding: "15px" }}>
                <h2>Confirm deletion</h2>
                <p>
                  Are you sure you want to delete the campaign template{" "}
                  <strong>"{selectedCampaignTemplate.templateName}"</strong>?
                </p>
                <div className="modal-footer">
                  <button
                    className="button secondary"
                    onClick={() => {
                      setShowDeleteConfirmModal(false);
                      setSelectedCampaignTemplate(null);
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="button"
                    style={{ backgroundColor: "#3f9f42", color: "white" }}
                    onClick={handleDeleteCampaignTemplate}
                    disabled={isLoading}
                  >
                    {isLoading ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* View Campaign Template Modal */}
          {showViewCampaignModal && selectedCampaignTemplate && (
            <div className="modal-backdrop">
              <div className="modal-content modal-large">
                {/* ✅ Header with close (✕) icon */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: "-22px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowViewCampaignModal(false);
                      setSelectedCampaignTemplate(null);
                      setExampleEmail("");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "22px",
                      cursor: "pointer",
                      color: "#6b7280",
                      fontWeight: "bold",
                    }}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                {/* Render ONLY Example Output */}
                <div className="modal-body">
                    <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>Example Output</h3>
                  <div
                    className="example-output-preview"
                    style={{
                      background: "#ffffff",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      height: "auto",
                      overflowY: "hidden",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: exampleEmail || "<p style='color: #9ca3af; text-align: center; padding: 40px 0;'>No example email available</p>"
                    }}
                  />
                </div>
                 {/* Modal Footer with Edit Button */}
                <div className="modal-footer" style={{ display: "flex", gap: "10px", justifyContent: "flex-end", padding: "16px", borderTop: "1px solid #e5e7eb" }}>
                  <button
                    onClick={() => {
                      setShowViewCampaignModal(false);
                      setSelectedCampaignTemplate(null);
                      setShowEditCampaignModal(true);
                      setEditCampaignForm({
                        templateName: selectedCampaignTemplate?.templateName || "",
                        aiInstructions: selectedCampaignTemplate?.aiInstructions || "",
                        placeholderListInfo: selectedCampaignTemplate?.placeholderListInfo || "",
                        masterBlueprintUnpopulated: selectedCampaignTemplate?.masterBlueprintUnpopulated || "",
                      });
                    }}
                    style={{
                      padding: "8px 16px",
                      background: "#3b82f6",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500"
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Campaign Modal */}
          {showEditCampaignModal && selectedCampaignTemplate && (
            <div className="modal-backdrop">
              <div className="modal-content modal-large">
                <h2>Advanced edit: {selectedCampaignTemplate.templateName}</h2>

                <div className="form-group">
                  <label>Template name</label>
                  <input
                    type="text"
                    value={editCampaignForm.templateName}
                    onChange={(e) =>
                      setEditCampaignForm({
                        ...editCampaignForm,
                        templateName: e.target.value,
                      })
                    }
                    placeholder="Template Name"
                  />
                </div>

                <div className="form-group">
                  <label>AI instructions</label>
                  <textarea
                    value={editCampaignForm.aiInstructions}
                    onChange={(e) =>
                      setEditCampaignForm({
                        ...editCampaignForm,
                        aiInstructions: e.target.value,
                      })
                    }
                    rows={6}
                    placeholder="AI Instructions for conversation"
                  />
                </div>

                <div className="form-group">
                  <label>Placeholder list</label>
                  <textarea
                    value={editCampaignForm.placeholderListInfo}
                    onChange={(e) =>
                      setEditCampaignForm({
                        ...editCampaignForm,
                        placeholderListInfo: e.target.value,
                      })
                    }
                    rows={4}
                    placeholder="{name}, {company}, {role}"
                  />
                </div>

                <div className="form-group">
                  <label>Master blueprint (unpopulated)</label>
                  <textarea
                    value={editCampaignForm.masterBlueprintUnpopulated}
                    onChange={(e) =>
                      setEditCampaignForm({
                        ...editCampaignForm,
                        masterBlueprintUnpopulated: e.target.value,
                      })
                    }
                    rows={10}
                    placeholder="Template with {placeholders}"
                  />
                </div>

                 <div className="form-group">
                  <label>Example Output Preview</label>
                  <div
                    style={{
                      background: "#ffffff",
                      padding: "20px",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      minHeight: "200px",
                      overflowY: "auto"
                    }}
                    dangerouslySetInnerHTML={{
                      __html: exampleEmail || "<p style='color: #9ca3af; text-align: center; padding: 40px 0;'>No example email loaded</p>"
                    }}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="button secondary"
                    onClick={() => {
                      setShowEditCampaignModal(false);
                      setEditCampaignForm({
                        templateName: "",
                        aiInstructions: "",
                        placeholderListInfo: "",
                        masterBlueprintUnpopulated: "",
                      });
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="button save-button"
                    onClick={handleUpdateCampaignTemplate}
                    disabled={isLoading || !editCampaignForm.templateName}
                  >
                    {isLoading ? "Updating..." : "Update template"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ✅ Show Campaign Builder Inline */
        <div style={{ padding: "4px 20px 20px" }}>
          {/* `key` forces a full remount when the user switches blueprint, so
              every piece of builder state is rebuilt from the new blueprint. */}
          <EmailCampaignBuilder
            key={activeBlueprintId ?? "new-blueprint"}
            selectedClient={effectiveUserId}
            onBeforeAiChatOpen={ensureCanOpenAiChat}
            onExitBuilder={handleExitBuilder}
            blueprintOptions={blueprintSwitcherOptions}
            activeBlueprintId={activeBlueprintId}
            onBlueprintChange={handleBlueprintSwitch}
            isSwitchingBlueprint={isSwitchingBlueprint}
          />
        </div>
      )}

      {/* Credit Check Modal */}
      <CreditCheckModal
        isOpen={showCreditModal || forceShowCreditModal}
        onClose={() => {
          setForceShowCreditModal(false);
          closeCreditModal();
        }}
        onSkip={() => {
          setForceShowCreditModal(false);
          handleSkipModal();
        }}
        credits={credits || 0}
        setTab={handleCreditModalTabChange}
      />
    </div>
  );
};

export default Template;

